import { ArrowUpRight, MonitorSmartphone, QrCode, Smartphone } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal } from "./Reveal";

const STR = {
  it: {
    kicker: "Sviluppo Web & App",
    title: "Guarda i siti che costruiamo. Dal vivo.",
    sub: "Vetrina interattiva con demo reali: sito per salone e pizzeria, installabili sul telefono come una vera app.",
    cta: "Esplora le demo",
    domain: "demapp.shop",
    appTitle: "Installa l'app Vetrina",
    appSub: "Gratis, senza App Store: si installa dal browser in 10 secondi.",
    android: {
      name: "Android",
      steps: ["Scansiona il QR con la fotocamera", "Apri il link in Chrome", "Tocca «Installa app» dal menu di Chrome"],
    },
    ios: {
      name: "iPhone · iOS",
      steps: ["Scansiona il QR con la fotocamera iPhone", "Il sito si apre in Safari", "Tocca Condividi, poi «Aggiungi alla schermata Home»"],
    },
  },
  de: {
    kicker: "Web- & App-Entwicklung",
    title: "Sieh dir unsere Websites an. Live.",
    sub: "Interaktive Vitrine mit echten Demos: Website für Salon und Pizzeria, auf dem Handy installierbar wie eine echte App.",
    cta: "Demos ansehen",
    domain: "demapp.shop",
    appTitle: "Vetrina-App installieren",
    appSub: "Kostenlos, ohne App Store: Installation direkt aus dem Browser in 10 Sekunden.",
    android: {
      name: "Android",
      steps: ["QR-Code mit der Kamera scannen", "Link in Chrome öffnen", "Im Chrome-Menü «App installieren» tippen"],
    },
    ios: {
      name: "iPhone · iOS",
      steps: ["QR-Code mit der iPhone-Kamera scannen", "Die Website öffnet sich in Safari", "Auf Teilen tippen, dann «Zum Home-Bildschirm»"],
    },
  },
  fr: {
    kicker: "Développement Web & App",
    title: "Regarde les sites qu'on construit. En direct.",
    sub: "Vitrine interactive avec de vraies démos : site pour salon et pizzeria, installables sur le téléphone comme une vraie app.",
    cta: "Explorer les démos",
    domain: "demapp.shop",
    appTitle: "Installe l'app Vetrina",
    appSub: "Gratuit, sans App Store : installation depuis le navigateur en 10 secondes.",
    android: {
      name: "Android",
      steps: ["Scanne le QR avec l'appareil photo", "Ouvre le lien dans Chrome", "Touche « Installer l'app » dans le menu de Chrome"],
    },
    ios: {
      name: "iPhone · iOS",
      steps: ["Scanne le QR avec l'appareil photo iPhone", "Le site s'ouvre dans Safari", "Touche Partager, puis « Sur l'écran d'accueil »"],
    },
  },
  en: {
    kicker: "Web & App Development",
    title: "See the websites we build. Live.",
    sub: "Interactive showcase with real demos: salon and pizzeria websites, installable on your phone like a real app.",
    cta: "Explore the demos",
    domain: "demapp.shop",
    appTitle: "Install the Vetrina app",
    appSub: "Free, no App Store: installs from the browser in 10 seconds.",
    android: {
      name: "Android",
      steps: ["Scan the QR with your camera", "Open the link in Chrome", "Tap “Install app” from the Chrome menu"],
    },
    ios: {
      name: "iPhone · iOS",
      steps: ["Scan the QR with the iPhone camera", "The site opens in Safari", "Tap Share, then “Add to Home Screen”"],
    },
  },
};

const InstallCard = ({ data, icon: Icon, testId, delay }) => (
  <Reveal delay={delay}>
    <div
      data-testid={testId}
      className="glass group h-full rounded-3xl p-7 transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/35"
    >
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-700/70 bg-slate-900/70">
          <Icon className="h-5 w-5 text-emerald-400" />
        </span>
        <h3 className="font-display text-xl font-bold tracking-tight text-slate-100">{data.name}</h3>
      </div>
      <div className="mt-6 flex items-center gap-6">
        <div className="shrink-0 rounded-2xl bg-white p-2.5 shadow-[0_10px_40px_rgba(0,0,0,0.45)] transition-transform duration-300 group-hover:scale-[1.03]">
          <img
            src="/img/qr-demapp.png"
            alt={`QR code app ${data.name} — demapp.shop`}
            className="h-32 w-32 sm:h-36 sm:w-36"
            loading="lazy"
          />
        </div>
        <ol className="space-y-3.5">
          {data.steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
              <span className="font-mono2 mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-[10px] font-semibold text-emerald-400">
                {i + 1}
              </span>
              {step}
            </li>
          ))}
        </ol>
      </div>
    </div>
  </Reveal>
);

export const WebAppBanner = () => {
  const { lang } = useLang();
  const s = STR[lang] || STR.it;

  return (
    <section id="web-app" data-testid="webapp-banner" className="relative py-10 sm:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <Reveal>
          <a
            href="https://www.demapp.shop"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="webapp-banner-link"
            className="group glass relative block overflow-hidden rounded-3xl transition-all duration-500 hover:border-emerald-500/40 hover:shadow-[0_30px_90px_rgba(16,185,129,0.15)]"
          >
            <div className="grid items-center lg:grid-cols-2">
              <div className="relative z-10 p-8 sm:p-12">
                <p className="font-mono2 flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-cyan-400">
                  <MonitorSmartphone className="h-4 w-4" />
                  {s.kicker}
                </p>
                <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight text-slate-50 sm:text-4xl">
                  {s.title}
                </h2>
                <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">{s.sub}</p>
                <span className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 px-6 py-3.5 text-sm font-bold text-slate-950 shadow-[0_0_30px_rgba(16,185,129,0.35)] transition-transform group-hover:scale-105">
                  {s.cta}
                  <span className="font-mono2 font-semibold">{s.domain}</span>
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </div>
              <div className="relative h-64 overflow-hidden lg:h-full lg:min-h-[22rem]">
                <img
                  src="/img/webapp-banner.png"
                  alt="Sviluppo web e app — demo siti per saloni e pizzerie"
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220] via-transparent to-transparent max-lg:bg-gradient-to-t" />
              </div>
            </div>
          </a>
        </Reveal>

        <div className="mt-6">
          <Reveal className="mb-6 flex items-center gap-3">
            <QrCode className="h-5 w-5 text-emerald-400" />
            <div>
              <h3 className="font-display text-xl font-bold tracking-tight text-slate-50">{s.appTitle}</h3>
              <p className="text-sm text-slate-500">{s.appSub}</p>
            </div>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-2">
            <InstallCard data={s.android} icon={Smartphone} testId="install-card-android" delay={0.1} />
            <InstallCard data={s.ios} icon={Smartphone} testId="install-card-ios" delay={0.2} />
          </div>
        </div>
      </div>
    </section>
  );
};
